import { useFetch, useCreate, useUpdate } from "../../../../../hooks/api";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import ContentEditor from "../../../../../modules/product_quality/ContentEditor2";
import {
  HIGHLIGHT_RECOMMENDED_LENGTH,
  TITLE_MAX_LENGTH,
  TITLE_RECOMMENDED_LENGTH,
} from "../../../../../modules/product_quality/constants";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaXmark } from "react-icons/fa6";
import { Badge } from "react-bootstrap";
import { createFileRoute } from "@tanstack/react-router";
import { Checkbox } from "../../../../../components/fields/base/Checkbox";
import { TextField } from "../../../../../components/fields/base/TextField";
import { css } from "../../../../../../styled-system/css";
import { VStack } from "../../../../../../styled-system/jsx";
import { Button } from "../../../../../components/core/basic/Button";
import Card from "../../../../../components/core/basic/Card";
import { PageContainer } from "../../../../../components/core/basic/PageContainer";
import { Tabs, TabList, Tab, TabPanel } from "../../../../../components/core/basic/Tabs";
import { MenuButton, Menu, MenuItem } from "../../../../../components/core/basic/Menu";
import { useAppSelector } from "../../../../../redux/hooks";
import TemplateHistory from "../../../../../modules/product_quality/components/TemplateHistory";
import ProductsPreview from "../../../../../modules/product_quality/components/ProductsPreview";
import Loading from "../../../../../components/core/blocks/Loading";
import ConfirmationModal from "../../../../../components/core/blocks/ConfirmationModal";
import TrellisBreadcrumb from "../../../../../components/core/blocks/Breadcrumb";
import ConversionLegend from "../../../../../modules/product_quality/components/ConversionLegend";
import { formatCurrency, formatNumber, formatPercent } from "../../../../../utils/formatNumber";
import { getBadgeVariant } from "../../../../../modules/product_quality/utils";

import SideMenuBottom from "../../../../../modules/product_quality/components/SideMenuBottom";
import axios from "axios";
import { getURLPrefix } from "../../../../../utils/getUrlPrefix";
import { successToast, errorToast } from "../../../../../utils/toasts";
import { useSimpleCan } from "../../../../../config/casl/casl";

const URL_PREFIX = getURLPrefix();

// API objects
// templateData - object from API (includes name, fields, revision)
// revision - (includes title, bullets array, keywords, description)
// fields - array of field names to update

// Frontend objects (extracted from templateData)
// templateName - name
// templateContent - revision but with flattened bullets array
// fieldsToUpdate - array of field names to update

const sections = [
  {
    label: ($) => $.product_quality["Product Title"],
    field: "title",
  },
  {
    label: ($) => $.product_quality["Bullet Points"],
    field: "feature_bullets_0",
  },
  {
    field: "feature_bullets_1",
  },
  {
    field: "feature_bullets_2",
  },
  {
    field: "feature_bullets_3",
  },
  {
    field: "feature_bullets_4",
  },
  {
    label: ($) => $.product_quality["Keywords"],
    field: "generic_keywords",
  },
  {
    label: ($) => $.product_quality["Description"],
    field: "description",
  },
  {
    label: ($) => $.product_quality["Highlight"],
    field: "highlight",
  },
];

const initialFields = sections.map(({ field }) => field);

const getRevisionFromTemplateContent = (templateContent) => ({
  title: templateContent?.title,
  feature_bullets: [
    templateContent?.feature_bullets_0,
    templateContent?.feature_bullets_1,
    templateContent?.feature_bullets_2,
    templateContent?.feature_bullets_3,
    templateContent?.feature_bullets_4,
  ],
  generic_keywords: templateContent?.generic_keywords,
  description: templateContent?.description,
  highlight: templateContent?.highlight,
});

const getTemplateContentFromRevision = (revision) => ({
  title: revision?.title,
  feature_bullets_0: revision?.feature_bullets?.[0],
  feature_bullets_1: revision?.feature_bullets?.[1],
  feature_bullets_2: revision?.feature_bullets?.[2],
  feature_bullets_3: revision?.feature_bullets?.[3],
  feature_bullets_4: revision?.feature_bullets?.[4],
  generic_keywords: revision?.generic_keywords,
  description: revision?.description,
  highlight: revision?.highlight,
});

const sortFn = (a, b) => b.cr - a.cr;

const tableStyles = css({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "medium",
  "& th, & td": {
    padding: "8px 12px",
    borderBottom: "1px solid {colors.border.default}",
    borderLeft: "1px solid {colors.border.default}",
    borderRight: "1px solid {colors.border.default}",
    textAlign: "right",
  },
  "& th": {
    borderTop: "1px solid {colors.border.default}",
    backgroundColor: "gray.50",
  },
  "& tbody tr": {
    _even: {
      backgroundColor: "gray.50",
    },
    _hover: {
      backgroundColor: "gray.100",
    },
  },
});

const ContentSection = ({
  product,
  templateContent,
  onContentChange,
  onSelectAllFields,
  fieldsToUpdate,
  onFieldsChange,
  highlightPhrases = [],
  isReadOnly = false,
}) => {
  const { t } = useTranslation(["product_quality"]);
  const allFieldsChecked = useMemo(() => fieldsToUpdate?.length === sections.length, [fieldsToUpdate]);
  return (
    <>
      {!isReadOnly && (
        <Checkbox
          label={t(($) => $.product_quality["Select the fields you wish to modify"])}
          value={allFieldsChecked}
          onChange={() => onSelectAllFields(!allFieldsChecked)}
        />
      )}
      {sections.map(({ field, label }, index) => (
        <div key={index} className={css({ marginBottom: "3rem" })}>
          {label && (
            <div
              className={css({
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "1.5rem",
              })}
            >
              <h4 style={{ marginTop: 0 }}>{t(label)}</h4>
              {field === "title" && <ConversionLegend />}
            </div>
          )}
          <ContentEditor
            startAdornment={
              !isReadOnly && <Checkbox value={fieldsToUpdate?.includes(field)} onChange={() => onFieldsChange(field)} />
            }
            richText={field === "description"}
            field={field}
            fieldIndex={index}
            content={templateContent?.[field] ?? ""}
            minLength={field === "title" || field === "highlight" ? 0 : 140}
            maxLength={field === "highlight" ? HIGHLIGHT_RECOMMENDED_LENGTH : TITLE_MAX_LENGTH}
            recommendedMaxLength={field === "title" ? TITLE_RECOMMENDED_LENGTH : undefined}
            setContent={onContentChange}
            variations={product?.variations}
            readOnly={!fieldsToUpdate?.includes(field) || isReadOnly}
            highlightPhrases={highlightPhrases}
          />
        </div>
      ))}
    </>
  );
};

const ProductsSection = ({ products, templateContent = {}, fieldsToUpdate = [] }) => {
  const { t } = useTranslation(["product_quality"]);
  const productsWithTemplateContent = products.map((product) => ({
    ...product,
    templateContent: {
      title: fieldsToUpdate.includes("title") ? templateContent.title : null,
      description: fieldsToUpdate.includes("description") ? templateContent.description : null,
      generic_keywords: fieldsToUpdate.includes("generic_keywords") ? templateContent.generic_keywords : null,
      feature_bullets_0: fieldsToUpdate.includes("feature_bullets_0") ? templateContent.feature_bullets_0 : null,
      feature_bullets_1: fieldsToUpdate.includes("feature_bullets_1") ? templateContent.feature_bullets_1 : null,
      feature_bullets_2: fieldsToUpdate.includes("feature_bullets_2") ? templateContent.feature_bullets_2 : null,
      feature_bullets_3: fieldsToUpdate.includes("feature_bullets_3") ? templateContent.feature_bullets_3 : null,
      feature_bullets_4: fieldsToUpdate.includes("feature_bullets_4") ? templateContent.feature_bullets_4 : null,
    },
  }));

  return (
    <>
      <h5 className={css({ marginBottom: "3rem" })}>
        {t(($) => $.product_quality["Changes will be applied to these products"])}
      </h5>
      <ProductsPreview products={productsWithTemplateContent} />
    </>
  );
};

const HistorySection = ({ templateId, setTemplateContent, setContentChanged }) => {
  const { t } = useTranslation(["product_quality"]);
  const onApply = (revision) => {
    setTemplateContent(getTemplateContentFromRevision(revision));
    setContentChanged(true);
  };
  return (
    <>
      <h5 className={css({ marginBottom: "3rem" })}>
        {t(($) => $.product_quality["Choose a previous version of this template to apply"])}
      </h5>
      <TemplateHistory templateId={templateId} onApply={onApply} />
    </>
  );
};

const ContentTemplate = () => {
  const { t } = useTranslation(["product_quality"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { templateId } = Route.useParams();
  const [_templateContent, setTemplateContent] = useState(null);
  const [tab, setTab] = useState("content");
  const [_fieldsToUpdate, setFieldsToUpdate] = useState(null);
  const [_templateName, setTemplateName] = useState(null);
  const [contentChanged, setContentChanged] = useState(false);
  const [editorMode, setEditorMode] = useState("edit");

  const [keyword, setKeyword] = useState(null);
  const [loadingSearchTerms, setLoadingSearchTerms] = useState(false);
  const [searchTerms, setSearchTerms] = useState(null);

  const queryClient = useQueryClient();
  const userCanUpdateProducts = useSimpleCan("update", "Product");

  const { data: templateData, isLoading: isTemplateDataLoading } = useFetch(
    ["content_template", templateId],
    `/merchandise/content_templates/${templateId}/`,
    {},
    {
      select: (data) => data?.data || {},
    },
  );

  const products = templateData?.products ?? [];
  const product = products[0] ?? null;

  const category = product?.product_category ?? "";
  const { data: categoryObj } = useFetch(
    ["categories", category],
    `/api/v2/categories/${category}`,
    {},
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !!category,
    },
  );
  const seoKeywords = useMemo(() => (templateData?.seo_keywords?.keywords ?? []).toSorted(sortFn), [templateData]);
  const seoSearchTerms = useMemo(
    () => (templateData?.seo_keywords?.search_terms ?? []).toSorted(sortFn),
    [templateData],
  );
  const hiddenKeywords = templateData?.hidden_keywords ?? [];

  const getSearchTerms = async (monogram, product_id) => {
    if (loadingSearchTerms) {
      return;
    }
    setEditorMode("metrics");
    setKeyword(monogram);
    setLoadingSearchTerms(true);

    const response = await axios.get(
      `${URL_PREFIX}/merchandise/search_terms?monogram=${monogram["keyword_text"]}&product_id=${product_id}`,
    );

    const sts = [];
    for (let i = 0; i < response.data.search_terms.length; i++) {
      const st = response.data.search_terms[i];

      st.ctr = 0;
      st.cr = 0;
      st.acos = 0;

      if (st.impressions > 0) {
        st.ctr = st.clicks / st.impressions;
      }
      if (st.clicks > 0) {
        st.cr = st.conversions / st.clicks;
      }
      if (st.cost > 0 && st.sales > 0) {
        st.acos = st.cost / st.sales;
      }
      sts.push(st);
    }
    sts.sort(function (a, b) {
      return b.impressions - a.impressions;
    });

    setSearchTerms(sts);
    setLoadingSearchTerms(false);
  };

  const templateContent = useMemo(() => {
    const revision = templateData?.revision?.title ? templateData?.revision : templateData?.initial_revision;
    return _templateContent || getTemplateContentFromRevision(revision);
  }, [_templateContent, templateData]);

  const initialRevision = useMemo(() => {
    return templateData?.initial_revision;
  }, [templateData]);

  const fieldsToUpdate = useMemo(() => {
    return _fieldsToUpdate || templateData?.fields || initialFields;
  }, [_fieldsToUpdate, templateData]);

  // Used to populate the keyword explorer
  const allContentElements = useMemo(() => {
    const cleanDescription = templateContent?.description?.replaceAll(/<[^>]+>/g, "");
    return {
      title: templateContent?.title,
      description: cleanDescription,
      genericKeywords: templateContent?.generic_keywords,
      featureBullet0: templateContent?.feature_bullets_0,
      featureBullet1: templateContent?.feature_bullets_1,
      featureBullet2: templateContent?.feature_bullets_2,
      featureBullet3: templateContent?.feature_bullets_3,
      featureBullet4: templateContent?.feature_bullets_4,
    };
  }, [
    templateContent.description,
    templateContent.title,
    templateContent.generic_keywords,
    templateContent.feature_bullets_0,
    templateContent.feature_bullets_1,
    templateContent.feature_bullets_2,
    templateContent.feature_bullets_3,
    templateContent.feature_bullets_4,
  ]);

  const highlightPhrases = useMemo(() => [...seoKeywords, ...seoSearchTerms], [seoKeywords, seoSearchTerms]);

  const templateName = useMemo(() => {
    if (_templateName === "") return "";
    return _templateName || templateData?.name || "";
  }, [_templateName, templateData]);

  const saveDate = templateData?.save_date ?? null;

  const onContentChange = (field, value) => {
    setTemplateContent({ ...templateContent, [field]: value });
    setContentChanged(true);
  };

  const onFieldsChange = (field) => {
    if (fieldsToUpdate.includes(field)) setFieldsToUpdate(fieldsToUpdate.filter((f) => f !== field));
    else setFieldsToUpdate([...fieldsToUpdate, field]);
    setContentChanged(true);
  };
  const onSelectAllFields = (selectAll) => {
    if (selectAll) {
      setFieldsToUpdate(initialFields);
    } else {
      setFieldsToUpdate([]);
    }
    setContentChanged(true);
  };

  const { mutate: resetTemplate } = useCreate(
    `/merchandise/content_templates/${templateId}/reset/`,
    {},
    {
      onSuccess: () => {
        successToast({
          title: t(($) => $.product_quality["Template Reset"]),
        });
        queryClient.invalidateQueries({
          queryKey: ["content_template"],
        });
        setTemplateContent(getTemplateContentFromRevision(initialRevision));
        setContentChanged(false);
      },
      onError: (error) => {
        errorToast({
          message: t(($) => $.product_quality["Failed to reset template. Please try again or contact support."]),
          error,
        });
      },
    },
  );

  const resetTemplateForm = () => {
    setContentChanged(false);
    setTemplateContent(getTemplateContentFromRevision(initialRevision));
    setFieldsToUpdate(initialFields);
  };

  const { mutate: saveTemplate } = useCreate(
    `/merchandise/content_templates/${templateId}/save/`,
    {},
    {
      onSuccess: () => {
        successToast({
          title: t(($) => $.product_quality["Template Saved"]),
        });
        queryClient.invalidateQueries({
          queryKey: ["content_template"],
        });
        setContentChanged(false);
      },
      onError: (error) => {
        errorToast({
          message: t(($) => $.product_quality["Failed to save template. Please try again or contact support."]),
          error,
        });
      },
    },
  );

  const { mutate: publishTemplate, isLoading: isPublishing } = useCreate(
    `/merchandise/content_templates/${templateId}/publish/`,
    {},
    {
      onSuccess: () => {
        successToast({
          title: t(($) => $.product_quality["Template submitted for publishing"]),
        });
        queryClient.invalidateQueries({
          queryKey: ["content_template"],
        });
      },
      onError: (error) => {
        errorToast({
          message: t(
            ($) => $.product_quality["Failed to submit template for publishing. Please try again or contact support."],
          ),
          error,
        });
      },
    },
  );

  const { mutate: saveTemplateName } = useUpdate(`/merchandise/content_templates/${templateId}/`, {});

  const payload = {
    fields: fieldsToUpdate,
    revision: getRevisionFromTemplateContent(templateContent),
  };

  if (isTemplateDataLoading) {
    return <Loading height={"300px"} fullPage={true} />;
  }

  return (
    <>
      <TrellisBreadcrumb
        items={[
          { name: t(($) => $.product_quality["Content"]), href: "/user/content/quality/org" },
          {
            name: `${categoryObj?.name || t(($) => $.product_quality["Products"])}`,
            href: `/user/content/quality/category/${product?.product_category ?? 0}`,
          },
          {
            name: t(($) => $.product_quality["Edit Content Template"]),
            href: `/user/content/templates/${templateId}`,
          },
        ]}
      />
      <PageContainer withBreadcrumb>
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: { base: "1fr", md: "1fr 3fr" },
            gap: "medium",
            fontSize: "medium",
          })}
        >
          <VStack gap="medium" alignItems="stretch">
            <Card>
              <h5 className={css({ marginBottom: "small" })}>{t(($) => $.product_quality["Attributes"])}</h5>
              {Object.entries(product?.variations ?? {}).map(([key, value]) => (
                <div
                  key={key}
                  className={css({
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "xsmall",
                  })}
                >
                  <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>
                  <span>{value}</span>
                  <span>{"{" + key + "}"}</span>
                </div>
              ))}
              {Object.entries(product?.variations ?? {}).length === 0 && (
                <span className={css({ color: "text.secondary" })}>{t(($) => $.product_quality["No attributes"])}</span>
              )}
            </Card>

            <SideMenuBottom
              getSearchTerms={getSearchTerms}
              product={product}
              seoKeywords={seoKeywords}
              seoSearchTerms={seoSearchTerms}
              allContentElements={allContentElements}
              initialHiddenKeywords={hiddenKeywords}
            />
          </VStack>

          <Card>
            {editorMode === "edit" && (
              <>
                <div className={css({ display: "flex", alignItems: "center", gap: "medium", marginBottom: "1rem" })}>
                  <TextField
                    aria-label={t(($) => $.product_quality["Template Name"])}
                    value={templateName}
                    onChange={setTemplateName}
                    onBlur={() => saveTemplateName({ name: templateName })}
                    isDisabled={!userCanUpdateProducts}
                  />
                  {userCanUpdateProducts && (
                    <div className={css({ display: "flex", gap: "medium", flexShrink: 0 })}>
                      {saveDate === null ?
                        <Button
                          onPress={() => {
                            resetTemplateForm();
                          }}
                          isDisabled={!contentChanged}
                        >
                          {t(($) => $.product_quality["Reset"])}
                        </Button>
                      : <ConfirmationModal
                          title={t(($) => $.product_quality["Reset Content"])}
                          confirmationMessage={t(
                            ($) =>
                              $.product_quality[
                                "You are about to reset the content for this product. This will remove all changes made to the content since the last save. Are you sure you want to continue?"
                              ],
                          )}
                          onConfirm={() => {
                            resetTemplate();
                          }}
                          confirmButtonText={t(($) => $.product_quality["Reset"])}
                          modalButtonText={t(($) => $.product_quality["Reset"])}
                          disabled={saveDate === null}
                          modelButtonStyle={{ margin: 0 }}
                        />
                      }
                      <Button
                        isDisabled={!contentChanged}
                        onPress={() => {
                          saveTemplate(payload);
                        }}
                      >
                        {t(($) => $.product_quality["Save"])}
                      </Button>
                      <ConfirmationModal
                        title={t(($) => $.product_quality["Publish Content"])}
                        confirmationMessage={
                          <>
                            <div>
                              {t(
                                ($) =>
                                  $.product_quality[
                                    "You are about to publish the content for this product. This will make the content live on the listing page of Amazon."
                                  ],
                              )}
                            </div>
                            <div className={css({ paddingTop: "12px" })}>
                              <strong>
                                {t(
                                  ($) =>
                                    $.product_quality[
                                      "NOTE: It may take up to 24 hours for the changes to reflect on your Amazon Listing page"
                                    ],
                                )}
                              </strong>
                            </div>
                            <div className={css({ paddingTop: "12px" })}>
                              {t(($) => $.product_quality["Are you sure you want to continue?"])}
                            </div>
                          </>
                        }
                        onConfirm={() => {
                          publishTemplate();
                        }}
                        confirmButtonText={t(($) => $.product_quality["Publish"])}
                        modalButtonText={t(($) => $.product_quality["Publish"])}
                        disabled={!saveDate}
                        loading={isPublishing}
                        loadingModalButtonText={t(($) => $.product_quality["Publishing..."])}
                        modelButtonStyle={{ margin: 0 }}
                      />
                    </div>
                  )}
                </div>
                <Tabs selectedKey={tab} onSelectionChange={setTab}>
                  <TabList>
                    <Tab id="content">{t(($) => $.product_quality["Content"])}</Tab>
                    <Tab id="products">{t(($) => $.product_quality["Products"])}</Tab>
                    <Tab id="history">{t(($) => $.product_quality["History"])}</Tab>
                  </TabList>
                  <TabPanel id="content">
                    <ContentSection
                      product={product}
                      templateContent={templateContent}
                      onContentChange={onContentChange}
                      onFieldsChange={onFieldsChange}
                      onSelectAllFields={onSelectAllFields}
                      fieldsToUpdate={fieldsToUpdate}
                      highlightPhrases={highlightPhrases}
                      isReadOnly={!userCanUpdateProducts}
                    />
                  </TabPanel>
                  <TabPanel id="products">
                    <ProductsSection
                      products={products}
                      fieldsToUpdate={fieldsToUpdate}
                      templateContent={templateContent}
                    />
                  </TabPanel>
                  <TabPanel id="history">
                    <HistorySection
                      templateId={templateId}
                      setTemplateContent={setTemplateContent}
                      setContentChanged={setContentChanged}
                    />
                  </TabPanel>
                </Tabs>
              </>
            )}

            {editorMode === "metrics" && (
              <>
                <div className={css({ display: "flex", justifyContent: "flex-end", padding: "12px 20px 0" })}>
                  <Button
                    variant="contentOnly"
                    onPress={() => setEditorMode("edit")}
                    aria-label={t(($) => $.product_quality["Close"])}
                    className={css({ fontSize: "20px" })}
                  >
                    <FaXmark />
                  </Button>
                </div>
                <div className={css({ padding: "20px" })}>
                  <h4 style={{ marginTop: 0 }}>
                    {t(($) => $.product_quality["Keyword Performance"])}{" "}
                    {keyword && <Badge bg={getBadgeVariant(keyword)}>{keyword["keyword_text"]}</Badge>}
                  </h4>
                  {!keyword && (
                    <div className={css({ textAlign: "center", fontSize: "medium" })}>
                      {t(($) => $.product_quality["Select a keyword to view performance"])}
                    </div>
                  )}
                  {keyword && (
                    <table className={tableStyles}>
                      <thead>
                        <tr>
                          <th>{t(($) => $.product_quality["Impressions"])}</th>
                          <th>{t(($) => $.product_quality["Clicks"])}</th>
                          <th>{t(($) => $.product_quality["CTR"])}</th>
                          <th>{t(($) => $.product_quality["Conversions"])}</th>
                          <th>{t(($) => $.product_quality["CR"])}</th>
                          <th>{t(($) => $.product_quality["Sales"])}</th>
                          <th>{t(($) => $.product_quality["ACoS"])}</th>
                          <th>{t(($) => $.product_quality["Cost"])}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{keyword.impressions}</td>
                          <td>{keyword.clicks}</td>
                          <td>{(keyword.ctr * 100).toFixed(2)}%</td>
                          <td>{keyword.conversions}</td>
                          <td>{(keyword.cr * 100).toFixed(2)}%</td>
                          <td>{formatCurrency(keyword.sales, { marketPlace })}</td>
                          <td>{(keyword.acos * 100).toFixed(2)}%</td>
                          <td>{formatCurrency(keyword.cost, { marketPlace })}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
                <div className={css({ padding: "8px 20px 20px" })}>
                  <h4 style={{ marginTop: 0 }}>{t(($) => $.product_quality["Associated Search Terms"])}</h4>
                  {!searchTerms && !loadingSearchTerms && (
                    <div className={css({ textAlign: "center", fontSize: "medium" })}>
                      {t(($) => $.product_quality["Select a keyword to view the search terms"])}
                    </div>
                  )}
                  {loadingSearchTerms && (
                    <div className={css({ textAlign: "center", fontSize: "medium" })}>
                      {t(($) => $.product_quality["Finding search terms..."])}
                    </div>
                  )}

                  {!loadingSearchTerms && searchTerms && product && (
                    <>
                      <p>
                        {t(($) => $.product_quality["Below are the search terms that contain the keyword "])}
                        <Badge bg={getBadgeVariant(keyword)}>{keyword["keyword_text"]}</Badge>
                        {t(
                          ($) =>
                            $.product_quality[
                              ", which have generated sales on the advertisements that contain this product ("
                            ],
                        )}
                        <strong>
                          {product?.sku} / {product?.asin}
                        </strong>
                        {")."}
                      </p>
                      <div
                        className={css({
                          maxHeight: "500px",
                          minHeight: "200px",
                          overflowY: "auto",
                        })}
                      >
                        <table className={tableStyles}>
                          <thead>
                            <tr>
                              <th className={css({ textAlign: "left" })}>
                                {t(($) => $.product_quality["Search Term"])}
                              </th>
                              <th>{t(($) => $.product_quality["Impressions"])}</th>
                              <th>{t(($) => $.product_quality["Clicks"])}</th>
                              <th>{t(($) => $.product_quality["CTR"])}</th>
                              <th>{t(($) => $.product_quality["CONVERSIONS"])}</th>
                              <th>{t(($) => $.product_quality["CR"])}</th>
                              <th>{t(($) => $.product_quality["Sales"])}</th>
                              <th>{t(($) => $.product_quality["Cost"])}</th>
                              <th>{t(($) => $.product_quality["ACOS"])}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {searchTerms.map((term, index) => (
                              <tr key={index}>
                                <td>
                                  <MenuButton label={term.keyword_text}>
                                    <Menu>
                                      <MenuItem id="add-to-media-plan">
                                        {t(($) => $.product_quality["Add keyword to Media Plan"])}
                                      </MenuItem>
                                    </Menu>
                                  </MenuButton>
                                </td>
                                <td>{formatNumber(term.impressions)}</td>
                                <td>{formatNumber(term.clicks)}</td>
                                <td>{formatPercent(term.ctr)}%</td>
                                <td>{formatNumber(term.conversions)}</td>
                                <td>{formatPercent(term.cr)}%</td>
                                <td>{formatCurrency(term.sales, { marketPlace })}</td>
                                <td>{formatCurrency(term.cost, { marketPlace })}</td>
                                <td>{formatPercent(term.acos)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </Card>
        </div>
      </PageContainer>
    </>
  );
};

export const Route = createFileRoute("/_authenticated/user/content/templates/$templateId")({
  component: ContentTemplate,
  staticData: { application: "Product" },
});
